import type { WebviewBridgeApi } from "@bt-mobile/shared-contracts";

import { isWebview } from "./isWebview";
import type { KeysOfUnion, MobileWebview } from "./types";

/** Raw window.buildertrend shape used only for proxy casts inside this file. */
type MockBridgeTarget = WebviewBridgeApi;

let proxyTarget: object = typeof window !== "undefined" ? window : {};
if (import.meta.env.DEV && import.meta.env.VITE_MOCK_WEBVIEW === "true") {
  // Only use the mock when there's no real webview — if RN has already injected
  // sendToNative onto window, we're in a real mobile environment and should use
  // window directly so _webNavigate and other native handlers work.

  if (!isWebview()) {
    proxyTarget = getDevOnlyWebviewMock()!;
  }
}

/**
 * Importable mobile singleton that delegates to mobile handlers on `window`.
 *
 * Uses a Proxy so property access is deferred to call-time, solving
 * initialization order (mobile injects onto `window` before JS runs,
 * and the dev mock assigns onto `window` asynchronously).
 *
 * Callers should still guard with `isWebview()` before invoking methods,
 * since handlers will be `undefined` outside a mobile webview (or dev mock).
 *
 * @example
 * ```ts
 * import { mobile } from "@btnet/utilities-webview";
 *
 * if (mobile.isWebview) {
 *   mobile.onGoBack();
 * }
 * ```
 */
export const mobile = new Proxy<MobileWebview>(proxyTarget as MobileWebview, {
  get(target, prop: KeysOfUnion<MobileWebview>) {
    if (prop === "isWebview") {
      return typeof (target as MockBridgeTarget).sendToNative !== "undefined";
    }
    if (prop === "reload") {
      return () => {
        (target as MockBridgeTarget).onReloadWebView?.();
        (target as MockBridgeTarget).onDataChanged?.();
      };
    }
    const value = (target as MockBridgeTarget)[prop as keyof MockBridgeTarget];
    if (typeof value === "function") {
      return value.bind(target);
    }
    return value;
  },
  has(target, prop) {
    return prop in target;
  },
});

/**
 * Returns mock native event handlers when VITE_MOCK_WEBVIEW is enabled in development.
 * Returns undefined otherwise, so the app behaves normally.
 */
function getDevOnlyWebviewMock() {
  if (!(import.meta.env.DEV && import.meta.env.VITE_MOCK_WEBVIEW === "true")) {
    return undefined;
  }

  /* eslint-disable no-console */
  return {
    sendToNative: (...args) => {
      console.log("[webviewMock] sendToNative called with args:", args);
    },
    onGoBack: function (...args): void {
      console.log("[webviewMock] onGoBack called with args:", args);
    },
    onGoBackImmediately: function (...args): void {
      console.log("[webviewMock] onGoBackImmediately called with args:", args);
    },
    onDataChanged: function (...args): void {
      console.log("[webviewMock] onDataChanged called with args:", args);
    },
    onShare: function (payload): void {
      console.log("[webviewMock] onShare called with payload:", payload);
    },
    onCopyToClipboard: function (text): void {
      console.log("[webviewMock] onCopyToClipboard called with text:", text);
    },
    onOpenJobPicker: function (screenProps?): void {
      console.log(
        "[webviewMock] onOpenJobPicker called with screenProps:",
        screenProps,
      );
    },
    setTabBarVisibility: function (visible, animated?): void {
      console.log(
        "[webviewMock] setTabBarVisibility called with visible:",
        visible,
        "and animated:",
        animated,
      );
    },
    onNavigationEvent: function (payload): void {
      console.log(
        "[webviewMock] onNavigationEvent called with payload:",
        payload,
      );
    },
    compressAndUpload: function (input, options) {
      console.log(
        "[webviewMock] compressAndUpload called with input/options:",
        input,
        options,
      );
      return Promise.resolve({
        tempFileId: "0",
        fileName: "photo.jpg",
        mimeType: "image/jpeg",
        size: 0,
        thumbnail: "",
      });
    },
    openFilePickerAttachments: function (options) {
      console.log(
        "[webviewMock] openFilePickerAttachments called with options:",
        options,
      );
      return Promise.resolve({
        files: [],
        showBuilder: true,
        showSubs: false,
        showOwner: false,
        notifyInternalUsers: false,
        notifySubs: false,
        notifyOwner: false,
      });
    },
    openFilePickerStandalone: function (options) {
      console.log(
        "[webviewMock] openFilePickerStandalone called with options:",
        options,
      );
      return Promise.resolve({ status: "saved", uploadedCount: 1 });
    },
  } satisfies MockBridgeTarget;
  /* eslint-enable no-console */
}
